import {
  MWS_INSPECTION_TEMPLATE_GROUP,
  MWS_INSPECTION_TEMPLATE_GROUPS,
  type MwsInspectionTemplateGroup,
} from '../constants';

/**
 * Чистые помощники сборки примечания инспекции (веб и сервер).
 *
 * Все фразы (основные + результат/кем принято/отмена) живут в БД
 * (mws_inspection_templates), различаются полем
 * group и показываются рядами-группами в модалке приёмки. Этот модуль:
 *  - buildInspectionComment — подстановка плейсхолдеров в тело шаблона;
 *  - ссылки на протоколы/акты (buildDocChips) — из привязанных документов.
 *
 * {sections}/{surveyor} подставляются из данных, {object}/{object_gen} — из
 * привязанных объектов инспекции (метки — inspectionObjectLabels.ts), прочие
 * плейсхолдеры остаются маркером «…» для ручной правки (в самом комментарии).
 */

/** Фраза-шаблон (подмножество строки mws_inspection_templates). */
export interface InspectionTemplateVariant {
  id: number;
  /** Группа-ряд в модалке приёмки (main/result/acceptor/cancel). */
  group: MwsInspectionTemplateGroup;
  label: string;
  body: string;
  /** true — системная (из сида), не удаляется из UI; false — пользовательская. */
  isSystem: boolean;
}

/**
 * Нормализовать значение колонки group (в БД — свободный text) к типу-объединению.
 * Неизвестное/пустое → main (безопасный дефолт-ряд).
 */
export function toTemplateGroup(value: string): MwsInspectionTemplateGroup {
  return (
    MWS_INSPECTION_TEMPLATE_GROUPS.find(group => group === value) ??
    MWS_INSPECTION_TEMPLATE_GROUP.MAIN
  );
}

/** Короткая подпись фразы-кнопки из тела (усечение по длине). */
export function makePhraseLabel(body: string): string {
  const text = body.trim().replace(/\s+/g, ' ');
  return text.length <= 24 ? text : `${text.slice(0, 23).trimEnd()}…`;
}

/**
 * Готовый текст примечания из тела шаблона:
 *  - {sections} → номера секций через «*» (формат РГН), «—» если нет;
 *  - {surveyor} → имя сюрвейера;
 *  - {object}/{object_gen} → объекты инспекции (метки привязанных сущностей
 *    через запятую; склонение не выполняется, _gen — исторический синоним);
 *  - {itp} → номер пункта ITP выбранной проверки инспекции;
 *  - {drawing} → чертежи инспекции через запятую (виртуальная колонка
 *    `drawings`, серверный агрегат withDrawings);
 *  - прочие плейсхолдеры ({pressure}/{value}/{model}/…) → маркер «…». Это не
 *    недоделка, а осознанные поля «вписать руками»: их в живых фразах девять.
 */
export function buildInspectionComment(params: {
  body: string;
  sections: string[];
  surveyorName: string;
  object?: string;
  /** Номер пункта ITP (mws_itp_items.no); нет проверки → маркер «…». */
  itp?: string;
  /**
   * Чертежи инспекции одной строкой — виртуальная колонка `drawings`.
   *
   * Источник зависит от специализации, и это решают сами связи: у трубной
   * инспекции чертёж придёт от спулов, у механической — от объекта, у
   * электрической — от системы или через кабели её оборудования. Плюс
   * привязанные вручную чертежи КД. Поэтому в шаблоне вставка ОДНА.
   */
  drawings?: string;
  /** Номера привязанных актов через запятую — {act_nums}. */
  actNums?: string;
  /** Тип проверки для акта (аббревиатура вида работ инспекции) — {act_type}. */
  actType?: string;
  /** Номера протоколов ОДНОГО метода через запятую — {protocol_nums}. */
  protocolNums?: string;
  /** Метод этих протоколов («UT», «Ходовые испытания», «НК») — {protocol_method}. */
  protocolMethod?: string;
}): string {
  const sections = params.sections.length > 0 ? params.sections.join('*') : '—';
  const object = params.object?.trim() ? params.object.trim() : '…';
  const itp = params.itp?.trim() ? params.itp.trim() : '…';
  const drawings = params.drawings?.trim() ? params.drawings.trim() : '…';
  const marker = (value?: string) => (value?.trim() ? value.trim() : '…');
  const text = params.body
    .replace(/\{sections\}/g, sections)
    .replace(/\{surveyor\}/g, params.surveyorName)
    .replace(/\{object(_gen)?\}/g, object)
    .replace(/\{itp\}/g, itp)
    .replace(/\{drawing\}/g, drawings)
    .replace(/\{act_nums\}/g, marker(params.actNums))
    .replace(/\{act_type\}/g, marker(params.actType))
    .replace(/\{protocol_nums\}/g, marker(params.protocolNums))
    .replace(/\{protocol_method\}/g, marker(params.protocolMethod));
  // Оставшиеся (несопоставленные) плейсхолдеры → видимый маркер для ручной правки.
  return text.replace(/\{[^}]+\}/g, '…');
}

/** Готовая фраза-кнопка (дописывается в конец комментария тапом). */
export interface InspectionChip {
  label: string;
  text: string;
}

/** Дописывает фразу чипа в конец текста (через пробел, без двойных пробелов). */
export function appendChip(current: string, text: string): string {
  const base = current.trimEnd();
  return base ? `${base} ${text}` : text;
}

/**
 * Убирает из текста последнюю вставку фразы — отмена вставки чипа.
 *
 * Ищет ПОСЛЕДНЕЕ вхождение: вставки снимаются в порядке «последняя первой», а
 * то, что дописали руками ПОСЛЕ вставки, сохраняется (в отличие от отката к
 * снимку всего текста). Пробел-разделитель перед фразой убирается вместе с ней.
 *
 * @param current текущий текст комментария
 * @param text вставленная ранее фраза (ровно та, что дал appendChip)
 * @returns текст без фразы либо null, если фразы в тексте больше нет —
 *          её удалили или правили руками, отменять нечего
 */
export function removeLastChip(current: string, text: string): string | null {
  if (!text) return null;
  const at = current.lastIndexOf(text);
  if (at === -1) return null;
  // Съедаем разделитель, который поставил appendChip, иначе останется двойной пробел.
  const from = at > 0 && current[at - 1] === ' ' ? at - 1 : at;
  return current.slice(0, from) + current.slice(at + text.length);
}

/**
 * Аббревиатура метода/вида работ из имени: «Ультразвуковой контроль (UT)» → «UT».
 * Нет скобок в конце — null (метод без короткого кода, напр. «Вакуумный тест»).
 */
export function extractMethodAbbr(workTypeName: string): string | null {
  const match = workTypeName.match(/\(([^)]+)\)\s*$/);
  return match ? match[1].trim() : null;
}

/** Минимум данных протокола для чипа-ссылки: номер + имя метода (вид работ). */
export interface DocChipProtocol {
  number?: string | null;
  method?: { name?: string | null } | null;
}

/** Минимум данных акта для чипа-ссылки: номер. */
export interface DocChipAct {
  number?: string | null;
}

/**
 * Кнопка-ссылка на документы инспекции: тело системной фразы + значения именно
 * этой кнопки. Подстановку делает вызывающий (buildInspectionComment) — так
 * doc-фразы проходят тот же движок, что и обычные, и в них работают {object} и
 * прочие плейсхолдеры инспекции.
 */
export type DocChip =
  | { kind: 'act'; label: string; body: string; nums: string }
  | { kind: 'protocol'; label: string; body: string; nums: string; method: string };

/**
 * Кнопки ряда «Протоколы, Акты» — по привязанным к инспекции документам.
 *
 * Протоколы группируются ПО МЕТОДУ: одна кнопка на метод, подпись — аббревиатура
 * («UT»), либо имя метода целиком, если аббревиатуры нет («Ходовые испытания»),
 * либо «НК», если метод не указан вовсе (он необязателен). Разбивка динамическая:
 * кнопки появляются только для методов, реально присутствующих на инспекции, —
 * поэтому отдельные шаблоны на каждый метод не нужны, хватает {protocol_method}.
 *
 * Тексты берутся из системных фраз группы docs (`doc_act` / `doc_protocol`), а не
 * зашиты здесь: формулировку правит РГН без деплоя. Учитываются только
 * ПРОНУМЕРОВАННЫЕ документы — ссылаться на безномерные не на что.
 *
 * @param protocols привязанные протоколы (нужны number + method.name)
 * @param acts привязанные акты (нужен number)
 * @param actPhrase системная фраза «Акт» (label кнопки + тело); нет — кнопки нет
 * @param protocolPhrase системная фраза «Протокол»; нет — кнопок протоколов нет
 */
export function buildDocChips(params: {
  protocols: DocChipProtocol[];
  acts: DocChipAct[];
  actPhrase?: { label: string; body: string };
  protocolPhrase?: { label: string; body: string };
}): DocChip[] {
  const chips: DocChip[] = [];

  if (params.protocolPhrase) {
    const byMethod = new Map<string, string[]>();
    for (const protocol of params.protocols) {
      const number = protocol.number?.trim();
      if (!number) continue;
      const methodName = protocol.method?.name ?? '';
      const label = extractMethodAbbr(methodName) || methodName || 'НК';
      byMethod.set(label, [...(byMethod.get(label) ?? []), number]);
    }
    for (const [method, numbers] of [...byMethod.entries()].sort((a, b) =>
      a[0].localeCompare(b[0])
    )) {
      chips.push({
        kind: 'protocol',
        label: method,
        body: params.protocolPhrase.body,
        nums: numbers.join(', '),
        method,
      });
    }
  }

  if (params.actPhrase) {
    const numbers = params.acts
      .map(act => act.number?.trim())
      .filter((number): number is string => Boolean(number));
    if (numbers.length > 0) {
      chips.push({
        kind: 'act',
        label: params.actPhrase.label,
        body: params.actPhrase.body,
        nums: numbers.join(', '),
      });
    }
  }

  return chips;
}
